import React from 'react';
import { useCallbackRef } from '../useCallbackRef/useCallbackRef.js';

const useUncontrolledState = ({ defaultValue, onChange, }) => {
    const state = React.useState(defaultValue);
    const [value] = state;
    const previous = React.useRef(value);
    const change = useCallbackRef(onChange);
    React.useEffect(() => {
        if (previous.current === value)
            return;
        change(value);
        previous.current = value;
    }, [value, previous, change]);
    return state;
};
const useControllableState = ({ value, defaultValue, onChange, }) => {
    const [_value, _setValue] = useUncontrolledState({
        defaultValue,
        onChange,
    });
    const isControlled = value !== undefined;
    const __value = isControlled ? value : _value;
    const update = useCallbackRef(onChange);
    const setter = React.useCallback(previous => {
        if (isControlled) {
            const setter = previous;
            const next = typeof previous === 'function' ? setter(value) : previous;
            if (next !== value)
                update(next);
        }
        else {
            _setValue(previous);
        }
    }, [isControlled, value, _setValue, update]);
    return [__value, setter];
};
/**
 * Rework основного хука `useControllableState`
 * Пока что экспериментальная и нестабильная
 */
const UNSTABLE_useControllableStateV2 = (parameters) => {
    const __previous = React.useRef(parameters.defaultValue);
    const [__value, __setValue] = React.useState(parameters.defaultValue);
    const value = parameters.value === undefined ? __value : parameters.value;
    const updater = useCallbackRef(parameters.onChange);
    React.useEffect(() => {
        if (__previous.current === __value)
            return;
        if (__value === undefined)
            return;
        updater(__value);
    }, [__value, updater]);
    /**
     * При переходе из контролируемого в неконтролируемый
     * сбрасываем состояние до undefined
     * Это нужно чтобы при сбросе через `form.resetFields()` из `antd`
     *
     */
    React.useLayoutEffect(() => {
        if (parameters.value === undefined) {
            __setValue(undefined);
        }
    }, [parameters.value]);
    const setter = useCallbackRef(p => {
        __setValue(p);
        __previous.current = value;
    });
    return [value, setter];
};

export { UNSTABLE_useControllableStateV2, useUncontrolledState as UNSTABLE_useUncontrolledState, useControllableState };
//# sourceMappingURL=useControllableState.js.map
